/**
 * The account's dictation vocabulary (`/api/v1/voice/`): the words Denker
 * should always get right, how each was misheard, and the Memory names it
 * suggests. Every call is silent: the voice settings show their own errors.
 */
import { apiRequest, apiRequestWithStatus } from "./client";

interface ApiSuccess<T> {
  status: "success";
  data: T;
}

export type VocabularyHeardAs = {
  id: string;
  text: string;
  /** A real word in the speech language: replaced only in a stored context. */
  real_word: boolean;
  /** The neighbouring words where the user corrected it, `[left, right]`. */
  contexts: [string, string][];
  source: "user" | "learned";
};

export type VocabularyWord = {
  id: string;
  text: string;
  real_word: boolean;
  source: "user" | "learned" | "memory";
  heard_as: VocabularyHeardAs[];
  updated_at: string;
};

export type VocabularySuggestionKind = "person" | "project" | "company";

export type VocabularySuggestion = {
  name: string;
  kind: VocabularySuggestionKind;
};

export type VocabularyLearnRequest = {
  word: string;
  word_real: boolean;
  heard: string;
  heard_real: boolean;
  left: string;
  right: string;
};

/** The server keeps at most this many characters of a word or mishearing. */
export const VOCABULARY_TEXT_MAX = 100;

const BASE = "/api/v1/voice/vocabulary/";

export async function listVocabulary(): Promise<VocabularyWord[]> {
  const res = await apiRequest<ApiSuccess<VocabularyWord[]>>(BASE, {
    silent: true,
  });
  return res.data;
}

/** Adds a word; an existing word with the same letters is returned instead,
 *  with `created` false. */
export async function addVocabularyWord(body: {
  text: string;
  real_word: boolean;
  source: VocabularyWord["source"];
}): Promise<{ word: VocabularyWord; created: boolean }> {
  const res = await apiRequestWithStatus<ApiSuccess<VocabularyWord>>(BASE, {
    method: "POST",
    body,
    silent: true,
  });
  return { word: res.data.data, created: res.status === 201 };
}

/** Renames a word; a 409 means another word already has that text. */
export async function renameVocabularyWord(
  wordId: string,
  body: { text: string; real_word: boolean },
): Promise<VocabularyWord> {
  const res = await apiRequest<ApiSuccess<VocabularyWord>>(
    `${BASE}${wordId}/`,
    { method: "PATCH", body, silent: true },
  );
  return res.data;
}

export async function removeVocabularyWord(wordId: string): Promise<void> {
  await apiRequest(`${BASE}${wordId}/`, { method: "DELETE", silent: true });
}

/** Adds a mishearing to a word (moving it from another word that had it);
 *  returns the word with its mishearings. */
export async function addHeardAs(
  wordId: string,
  body: { text: string; real_word: boolean },
): Promise<VocabularyWord> {
  const res = await apiRequest<ApiSuccess<VocabularyWord>>(
    `${BASE}${wordId}/heard-as/`,
    { method: "POST", body, silent: true },
  );
  return res.data;
}

/** Removes a mishearing; returns the word with the ones left. */
export async function removeHeardAs(
  wordId: string,
  heardId: string,
): Promise<VocabularyWord> {
  const res = await apiRequest<ApiSuccess<VocabularyWord>>(
    `${BASE}${wordId}/heard-as/${heardId}/`,
    { method: "DELETE", silent: true },
  );
  return res.data;
}

/** Records one correction: the word, the mishearing and its neighbours. */
export async function learnVocabulary(
  body: VocabularyLearnRequest,
): Promise<VocabularyWord> {
  const res = await apiRequest<ApiSuccess<VocabularyWord>>(`${BASE}learn/`, {
    method: "POST",
    body,
    silent: true,
  });
  return res.data;
}

export async function listVocabularySuggestions(): Promise<
  VocabularySuggestion[]
> {
  const res = await apiRequest<ApiSuccess<VocabularySuggestion[]>>(
    `${BASE}suggestions/`,
    { silent: true },
  );
  return res.data;
}

export async function dismissVocabularySuggestion(name: string): Promise<void> {
  await apiRequest(`${BASE}suggestions/dismiss/`, {
    method: "POST",
    body: { name },
    silent: true,
  });
}
